import {
    Box,
    Container,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {SearchInput} from "../Search/SearchDialog";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import TableRowsIcon from "@mui/icons-material/TableRows";
import React from "react";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import {useTranslation} from "react-i18next";

export const HomeLayout_DISPLAY_MODE = {
    LIST: "list",
    GRID: "grid"
}

HomeLayout.propTypes = {
    title: PropTypes.string,
    titleElement: PropTypes.element,
    content: PropTypes.array,
    aside: PropTypes.any,
    toolbarElms: PropTypes.any,
    tabs: PropTypes.node,
    forceDisplay: PropTypes.oneOf(Object.values(HomeLayout_DISPLAY_MODE)),
    // params : event
    onSubmit: PropTypes.func,
    onScrollHitBottom: PropTypes.func,
    hasMoreData: PropTypes.bool,
    loader: PropTypes.element,
    noDataText: PropTypes.string,
}

HomeLayout.defaultProps = {
    title: "",
    content: [],
    aside: undefined,
    tabs: undefined,
    toolbarElms: undefined,
    onSubmit: () => null,
    onScrollHitBottom: () => null,
    hasMoreData: false,
    loader: <LinearProgress sx={{m: 1}}/>
}

export default function HomeLayout(props) {
    const {t} = useTranslation('common');
    const {title, content, aside, tabs, toolbarElms, onSubmit, onScrollHitBottom, hasMoreData, loader} = props;
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up("md"));
    const [display, setDisplay] = React.useState(props.forceDisplay ?? HomeLayout_DISPLAY_MODE.GRID);

    const handleChangeDisplayMode = (event, newValue) => {
        if (newValue) {
            setDisplay(props.forceDisplay ?? newValue);
        }
    }

    return (
        <Container
            maxWidth="xl"
            sx={{
                flexGrow: 1,
                margin: theme.spacing(2, 0, 2, 0),
                display: "flex"
            }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={9} xl={9}>
                    <Paper sx={{height: "100%", p: 2}} elevation={0}>
                        <Stack spacing={1} height="100%" direction="column">
                            {props.titleElement ?? (
                                <Typography ml={2} variant="h6" fontSize={22} fontWeight="bold" color="textSecondary">
                                    {title.toUpperCase()}
                                </Typography>
                            )}
                            <Toolbar sx={{
                                borderBottom: `solid 2px ${theme.palette.secondary.dark}`,
                                borderTop: `solid 2px ${theme.palette.secondary.dark}`,
                                justifyContent: "space-between"
                            }}>
                                <SearchInput onSubmit={(event) => onSubmit(event)}/>
                                <Stack direction="row" spacing={1} ml={1}>
                                    {toolbarElms}
                                    {!props.forceDisplay && mdUp && (
                                        <ToggleButtonGroup
                                            value={display}
                                            size="small"
                                            exclusive
                                            onChange={handleChangeDisplayMode}
                                        >
                                            <ToggleButton value={HomeLayout_DISPLAY_MODE.GRID}>
                                                <ViewComfyIcon sx={{color: theme.palette.text.secondary}}/>
                                            </ToggleButton>
                                            <ToggleButton value={HomeLayout_DISPLAY_MODE.LIST}>
                                                <TableRowsIcon sx={{color: theme.palette.text.secondary}}/>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    )}
                                </Stack>
                            </Toolbar>
                            {tabs}
                            <Box bgcolor={theme.palette.background.menu} p={1} flexGrow={1}>
                                <InfiniteScroll
                                    next={onScrollHitBottom}
                                    hasMore={hasMoreData}
                                    dataLength={content.length}
                                    style={{overflow: "hidden"}}
                                    loader={loader}
                                >
                                    <Grid container justifyContent="center">
                                        {
                                            content.length !== 0 ?
                                                (content.map((value, index) => (
                                                    <Grid
                                                        key={`grid-item-${index}`}
                                                        item
                                                        xs={12} sm={12}
                                                        md={display === "list" ? 12 : 6}
                                                        xl={display === "list" ? 12 : 4}
                                                    >
                                                        {value}
                                                    </Grid>
                                                )))
                                                : (
                                                    <Typography variant="h5" color="text.secondary">
                                                        {props.noDataText}
                                                    </Typography>
                                                )
                                        }
                                    </Grid>
                                </InfiniteScroll>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item xs md lg={3} xl={3}>
                    <Paper elevation={0}>
                        <Stack p={1} spacing={1}>
                            {aside}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    )
}